import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, unref as _unref, createVNode as _createVNode } from "vue";
const _hoisted_1 = { class: "p-4 w-full space-y-4" };
const _hoisted_2 = { class: "flex space-x-2 items-center m-auto" };
const _hoisted_3 = { class: "p-4 m-auto" };
const _hoisted_4 = { class: "grid grid-cols-7 gap-4" };
const _hoisted_5 = ["onClick"];
const _hoisted_6 = ["onClick"];
const _hoisted_7 = { class: "flex space-x-4 p-4" };
import { ref, computed, watchEffect, watch } from "vue";
import ActionButton from '@vuedir/components/ActionButton';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ColorPicker',
    props: {
        defaultColor: {
            type: String,
            required: true
        },
    },
    emits: ["applyColor", "cancel"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        // HSL to HEX conversion
        const hslToHex = (h, s, l) => {
            s /= 100;
            l /= 100;
            const k = (n) => (n + h / 30) % 12;
            const a = s * Math.min(l, 1 - l);
            const f = (n) => Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
            return `#${f(0).toString(16).padStart(2, "0")}${f(8).toString(16).padStart(2, "0")}${f(4).toString(16).padStart(2, "0")}`;
        };
        // Function to convert HEX to HSL
        const hexToHsl = (hex) => {
            let r, g, b;
            if (hex.length === 4) {
                hex = hex.split("").map(c => c + c).join("");
            }
            r = parseInt(hex.substr(1, 2), 16) / 255;
            g = parseInt(hex.substr(3, 2), 16) / 255;
            b = parseInt(hex.substr(5, 2), 16) / 255;
            const min = Math.min(r, g, b);
            const max = Math.max(r, g, b);
            const delta = max - min;
            let h = 0;
            const l = (min + max) / 2;
            let s = 0;
            if (delta !== 0) {
                if (max === r) {
                    h = (g - b) / delta;
                }
                else if (max === g) {
                    h = (b - r) / delta + 2;
                }
                else {
                    h = (r - g) / delta + 4;
                }
                s = delta / (1 - Math.abs(2 * l - 1));
            }
            return {
                h: Math.round(h * 60),
                s: s * 100,
                l: l * 100,
            };
        };
        // Convert the default HEX color to HSL
        const { h, s, l } = hexToHsl(props.defaultColor);
        const hue = ref(h);
        const saturation = ref(s / 100);
        const lightness = ref(l / 100);
        const selectedColor = ref(props.defaultColor);
        // Computed property to dynamically update the selected color
        const selectedColorComputed = computed(() => {
            return hslToHex(hue.value, saturation.value * 100, lightness.value * 100);
        });
        // Function to set the selected color (when clicking on the color palette)
        const setSelectedColor = (color) => {
            selectedColor.value = color;
            const { h, s, l } = hexToHsl(color);
            hue.value = h;
            saturation.value = s / 100;
            lightness.value = l / 100;
        };
        // Watcher to update selectedColor whenever hue changes
        watchEffect(() => {
            selectedColor.value = selectedColorComputed.value;
        });
        // Function to handle picking a color from the saturation-lightness box
        let isPicking = false;
        const startPicking = (e) => {
            isPicking = true;
            updateColor(e); // Ensure immediate response
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", stopPicking);
        };
        const stopPicking = () => {
            isPicking = false;
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", stopPicking);
        };
        // Function to update the saturation and lightness based on mouse position
        const updateColor = (e) => {
            const box = document.querySelector('.relative.w-full.h-40.rounded-md.overflow-hidden.cursor-crosshair');
            if (!box)
                return;
            const rect = box.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const newSaturation = Math.min(Math.max(x / rect.width, 0), 1);
            const newLightness = 1 - Math.min(Math.max(y / rect.height, 0), 1);
            saturation.value = newSaturation;
            lightness.value = newLightness;
        };
        let animationFrameId = null;
        const onMouseMove = (e) => {
            if (!isPicking)
                return;
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            animationFrameId = requestAnimationFrame(() => updateColor(e));
        };
        // Computed property to dynamically set the background of the hue slider
        const hueSliderBackground = computed(() => {
            return `linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))`;
        });
        const hexInput = ref(props.defaultColor);
        // Function to update color from HEX input
        // Function to update color from HEX input
        const updateColorFromHex = () => {
            const newHex = hexInput.value.trim();
            if (newHex.length === 7) {
                if (/^#([0-9A-Fa-f]{3}){1,2}$/.test(newHex)) {
                    const { h, s, l } = hexToHsl(newHex);
                    hue.value = h;
                    saturation.value = s / 100;
                    lightness.value = l / 100;
                    selectedColor.value = newHex;
                }
            }
        };
        // Sync hex input when selectedColor changes
        watch(selectedColor, (newHex) => {
            hexInput.value = newHex;
        });
        // Predefined color shades
        const colors = [
            { name: 'Red', shades: [{ hex: '#ffe5e5' }, { hex: '#ff6666' }, { hex: '#ff0000' }] },
            { name: 'Orange', shades: [{ hex: '#ffeacc' }, { hex: '#ffb366' }, { hex: '#ff8000' }] },
            { name: 'Yellow', shades: [{ hex: '#fff9c4' }, { hex: '#ffec80' }, { hex: '#ffd700' }] },
            { name: 'Green', shades: [{ hex: '#d4f8d4' }, { hex: '#66ff66' }, { hex: '#00ff00' }] },
            { name: 'Blue', shades: [{ hex: '#cce5ff' }, { hex: '#66b2ff' }, { hex: '#007bff' }] },
            { name: 'Purple', shades: [{ hex: '#e6ccff' }, { hex: '#b366ff' }, { hex: '#8000ff' }] },
            { name: 'Pink', shades: [{ hex: '#ffd9e6' }, { hex: '#ff66b2' }, { hex: '#ff1493' }] }
        ];
        const neutralColors = [
            { name: 'White', hex: '#ffffff' },
            { name: 'Light Gray', hex: '#d0d0d0' },
            { name: 'Gray', hex: '#808080' },
            { name: 'Dark Gray', hex: '#505050' },
            { name: 'Charcoal', hex: '#202020' },
            { name: 'Almost Black', hex: '#080808' },
            { name: 'Black', hex: '#000000' }
        ];
        const emit = __emit;
        const handleCancel = () => {
            emit('cancel');
        };
        const handleApply = () => {
            emit('applyColor', selectedColor.value);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createCommentVNode(" Saturation & Lightness Box "),
                    _createElementVNode("div", {
                        class: "relative w-full h-40 rounded-md overflow-hidden cursor-crosshair",
                        style: _normalizeStyle({ background: 'hsl(' + hue.value + ', 100%, 50%)' }),
                        onMousedown: startPicking
                    }, [
                        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "absolute w-full h-full bg-gradient-to-r from-white to-transparent" }, null, -1 /* CACHED */)),
                        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "absolute w-full h-full bg-gradient-to-t from-black to-transparent" }, null, -1 /* CACHED */)),
                        _createElementVNode("div", {
                            class: "absolute w-4 h-4 border-2 border-white rounded-full shadow",
                            style: _normalizeStyle({ left: saturation.value * 100 + '%', top: (1 - lightness.value) * 100 + '%' })
                        }, null, 4 /* STYLE */)
                    ], 36 /* STYLE, NEED_HYDRATION */),
                    _createElementVNode("div", _hoisted_2, [
                        _createCommentVNode(" Color Preview "),
                        _createElementVNode("div", {
                            class: "h-10 w-10 rounded-full border border-gray-300",
                            style: _normalizeStyle({ backgroundColor: selectedColor.value })
                        }, null, 4 /* STYLE */),
                        _createCommentVNode(" Hue Slider "),
                        _withDirectives(_createElementVNode("input", {
                            type: "range",
                            min: "0",
                            max: "360",
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((hue).value = $event)),
                            class: "flex-grow h-2 appearance-none rounded-md cursor-pointer",
                            style: _normalizeStyle({ background: hueSliderBackground.value })
                        }, null, 4 /* STYLE */), [
                            [_vModelText, hue.value]
                        ])
                    ]),
                    _createCommentVNode(" Hex Input "),
                    _withDirectives(_createElementVNode("input", {
                        type: "text",
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((hexInput).value = $event)),
                        class: "w-full p-2 border rounded-md text-gray-800 text-center",
                        onInput: updateColorFromHex
                    }, null, 544 /* NEED_HYDRATION, NEED_PATCH */), [
                        [_vModelText, hexInput.value]
                    ])
                ]),
                _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(colors, (color) => {
                            return _createElementVNode("div", {
                                key: color.name,
                                class: "space-y-4"
                            }, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(color.shades, (shade) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                        key: shade.hex,
                                        class: "w-8 h-8 rounded-md cursor-pointer",
                                        style: _normalizeStyle({ backgroundColor: shade.hex }),
                                        onClick: ($event) => (setSelectedColor(shade.hex))
                                    }, null, 12 /* STYLE, PROPS */, _hoisted_5));
                                }), 128 /* KEYED_FRAGMENT */))
                            ]);
                        }), 64 /* STABLE_FRAGMENT */)),
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(neutralColors, (neutralColor) => {
                            return _createElementVNode("div", {
                                key: neutralColor.hex
                            }, [
                                _createElementVNode("div", {
                                    class: _normalizeClass(["w-8 h-8 rounded-md cursor-pointer", [
                                            neutralColor.hex === '#ffffff' ? 'border border-gray-300' : ''
                                        ]]),
                                    style: _normalizeStyle({ backgroundColor: neutralColor.hex }),
                                    onClick: ($event) => (setSelectedColor(neutralColor.hex))
                                }, null, 14 /* CLASS, STYLE, PROPS */, _hoisted_6)
                            ]);
                        }), 64 /* STABLE_FRAGMENT */))
                    ])
                ]),
                _createElementVNode("div", _hoisted_7, [
                    _createVNode(_unref(ActionButton), {
                        class: "btn btn--secondary w-full",
                        "data-add-to-cart": "",
                        "aria-label": 'cancel color change',
                        label: 'Cancel',
                        onClick: handleCancel
                    }),
                    _createVNode(_unref(ActionButton), {
                        class: "btn btn--primary w-full",
                        "data-add-to-cart": "",
                        "aria-label": 'apply color',
                        label: 'Apply',
                        onClick: handleApply
                    })
                ])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
